import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useLocation, useSearchParams, useNavigationType } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import Footer from '../../component/Footer';
import Header from '../../component/Header';
import Productfilter from '../../component/Productfilter';
import Productmobilefilter from '../../component/Productmobilefilter';
import { addToWishlist } from "../../action";
import "./Products.css";
import InfiniteProducts from '../../component/Common/InfiniteProducts';
import { useGetSearchProductsInfiniteQuery } from '../../store/api/productsApi';

const per_page = 40;

export default function Search() {
  const { id, catId, vendorName } = useParams();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const isNewArrivals = location.pathname === '/new-arrivals';
  const isTopPicks = location.pathname === '/top-picks';
  
  // Create unique page key for filter storage (professional approach like Zalando/ASOS)
  const pageKey = useMemo(() => {
    if (isNewArrivals) return 'new-arrivals';
    if (isTopPicks) return 'top-picks';
    if (vendorName) return `vendor-${vendorName.toUpperCase()}`;
    if (id || catId) return `search-${id || catId}`;
    return 'search-default';
  }, [isNewArrivals, isTopPicks, vendorName, id, catId]);

  const filterStorageKey = `searchFilters_${pageKey}`;
  
  const urlGender = searchParams.get('gender');
  const discountQuery = searchParams.get('discount');
  const normalizedDiscount = discountQuery === 'yes' ? 'yes' : discountQuery === 'no' ? 'no' : "";
  const urlPage = Math.max(1, Number(searchParams.get('page') || 1) || 1);
  const wishlistItems = useSelector(state => state?.cart?.wishlistItems);
  // Use only explicit gender from URL (avoid hidden Redux filter on brand pages)
  const genderData = urlGender || undefined;

  const didNormalizePageRef = useRef(false);

  // Disable browser's automatic scroll restoration so we control it ourselves
  useEffect(() => {
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    return () => {
      if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);

  // Nëse hapet direkt një URL me ?page>1 (p.sh. rifreskim),
  // dhe nuk kemi state për t'u kthyer mbrapa nga produkti,
  // reseto page në 1 që lista të sillet si hapje e re.
  useEffect(() => {
    if (didNormalizePageRef.current) return;
    if (navigationType !== 'POP') return;
    if (urlPage <= 1) return;

    let hasScrollState = false;
    let hasProductState = false;
    try {
      hasScrollState = !!sessionStorage.getItem('plp_scrollY');
      hasProductState = !!sessionStorage.getItem('plp_productId');
    } catch {}

    // Nëse s'ka asnjë state për kthim mbrapa, trajtoje si rifreskim/hapje direkte
    if (!hasScrollState && !hasProductState) {
      didNormalizePageRef.current = true;
      const next = new URLSearchParams(searchParams);
      next.set('page', '1');
      setSearchParams(next, { replace: true });
    }
  }, [navigationType, urlPage, searchParams, setSearchParams]);

  // Restore filters from location.state or sessionStorage if available (when navigating back from product details)
  const savedFilters = location.state?.filters || (() => {
    try {
      const saved = sessionStorage.getItem(filterStorageKey);
      if (saved) {
        const parsedFilters = JSON.parse(saved);
        // Check if filters are older than 30 minutes (1800000 ms)
        const maxAge = 30 * 60 * 1000; // 30 minutes
        const now = Date.now();
        if (parsedFilters.timestamp && (now - parsedFilters.timestamp) > maxAge) {
          // Filters are too old, clear them
          sessionStorage.removeItem(filterStorageKey);
          return null;
        }
        return parsedFilters;
      }
      return null;
    } catch {
      return null;
    }
  })();
  const hasRestoredFilters = useRef(false);
  const lastLocationKey = useRef(location.key);

  // Filter states - initialize from location.state if available, otherwise empty
  const [categoryCheck, setCategoryCheck] = useState(() => {
    return savedFilters?.categoryCheck || [];
  });
  const [brandCheck, setBrandCheck] = useState(() => {
    return savedFilters?.brandCheck || [];
  });
  const [sizeCheck, setSizeCheck] = useState(() => {
    return savedFilters?.sizeCheck || [];
  });
  const [gtPrice, setGtPrice] = useState(() => {
    return savedFilters?.gtPrice;
  });
  const [ltPrice, setLtPrice] = useState(() => {
    return savedFilters?.ltPrice;
  });
  const [sortPrice, setSortPrice] = useState(() => {
    return savedFilters?.sortPrice || "";
  });
  const [offers, setOffers] = useState(() => {
    return savedFilters?.offers !== undefined ? savedFilters.offers : normalizedDiscount;
  });
  const [selectedGender, setSelectedGender] = useState(() => {
    return savedFilters?.selectedGender !== undefined ? savedFilters.selectedGender : undefined;
  });

  // Update offers state when discount query param changes (but not if we restored filters)
  useEffect(() => {
    // Skip if we've restored filters from location.state
    if (hasRestoredFilters.current) {
      return;
    }
    
    if (discountQuery === null) {
      setOffers((prev) => (prev ? "" : prev));
      return;
    }
    if (normalizedDiscount) {
      setOffers(normalizedDiscount);
    }
  }, [discountQuery, normalizedDiscount]);

  // Track previous page key to detect page changes
  const prevPageKeyRef = useRef(pageKey);
  
  // Restore filters when location.state changes (e.g., when navigating back)
  useEffect(() => {
    const currentFilters = location.state?.filters;
    const locationKeyChanged = lastLocationKey.current !== location.key;
    const pageKeyChanged = prevPageKeyRef.current !== pageKey;
    
    // Update trackers
    if (locationKeyChanged) {
      lastLocationKey.current = location.key;
    }
    if (pageKeyChanged) {
      prevPageKeyRef.current = pageKey;
      // Clear filters when page changes (unless coming from product details)
      if (!currentFilters) {
        setCategoryCheck([]);
        setBrandCheck([]);
        setSizeCheck([]);
        setGtPrice(undefined);
        setLtPrice(undefined);
        setSortPrice('');
        setOffers("");
        setSelectedGender(undefined);
        hasRestoredFilters.current = false;
        return;
      }
    }
    
    // Try to restore from location.state first, then fallback to sessionStorage
    let filtersToRestore = currentFilters;
    
    if (!filtersToRestore) {
      // Fallback to sessionStorage (for browser back button) - page-specific
      try {
        const savedFiltersStr = sessionStorage.getItem(filterStorageKey);
        if (savedFiltersStr) {
          const parsedFilters = JSON.parse(savedFiltersStr);
          // Check if filters are older than 30 minutes (1800000 ms)
          const maxAge = 30 * 60 * 1000; // 30 minutes
          const now = Date.now();
          if (parsedFilters.timestamp && (now - parsedFilters.timestamp) > maxAge) {
            // Filters are too old, clear them
            sessionStorage.removeItem(filterStorageKey);
            filtersToRestore = null;
          } else {
            filtersToRestore = parsedFilters;
          }
        }
      } catch (e) {
        console.warn('Failed to restore filters from sessionStorage:', e);
      }
    }
    
    // Restore filters if we have them and location changed (navigated back)
    if (filtersToRestore && locationKeyChanged) {
      setCategoryCheck(filtersToRestore.categoryCheck || []);
      setBrandCheck(filtersToRestore.brandCheck || []);
      setSizeCheck(filtersToRestore.sizeCheck || []);
      setGtPrice(filtersToRestore.gtPrice);
      setLtPrice(filtersToRestore.ltPrice);
      setSortPrice(filtersToRestore.sortPrice || "");
      setOffers(filtersToRestore.offers !== undefined ? filtersToRestore.offers : normalizedDiscount);
      setSelectedGender(filtersToRestore.selectedGender);
      hasRestoredFilters.current = true;
    }
  }, [location.state, location.key, normalizedDiscount, pageKey, filterStorageKey]);

  const displayWidth = window.innerWidth;
  const listContainerRef = useRef(null);

  // Build query params for RTK Query - use useMemo to ensure reactivity
  const productsQueryParams = useMemo(() => {
    const mergedCatId = [...new Set(categoryCheck.flat())];
    const vendorNameCaps = vendorName?.toUpperCase();
    
    // Use brandCheck if brands are selected, otherwise use vendorName if available
    let brandsToUse = [];
    if (brandCheck.length > 0) {
      brandsToUse = brandCheck;
    } else if (vendorNameCaps) {
      brandsToUse = [vendorNameCaps];
    } else if (id) {
      // /search/:id expects backend /scrollbox to search by first "brands" entry (searchKey)
      brandsToUse = [String(id)];
    }
    
    // Use selectedGender only when user explicitly picks it, otherwise URL gender only.
    const genderToUse = selectedGender || genderData;
    
    return {
      catAndGender: genderToUse,
      brands: brandsToUse,
      sizes: sizeCheck,
      gtPrice,
      ltPrice,
      sort: sortPrice,
      id,
      catId: [catId],
      per_page,
      discount: offers,
      selectedGender: genderToUse, // Use selectedGender from filter if available, otherwise genderData
      selectedCat: mergedCatId,
      newIn: isNewArrivals,
      selectionForYou: isTopPicks
    };
  }, [categoryCheck, vendorName, genderData, brandCheck, sizeCheck, gtPrice, ltPrice, sortPrice, id, catId, offers, selectedGender, isNewArrivals, isTopPicks]);


  const {
    data: productsResponse,
    isLoading,
    isFetching,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage
  } = useGetSearchProductsInfiniteQuery(productsQueryParams, {
    skip: !vendorName && !id && !isNewArrivals && !isTopPicks,
    refetchOnMountOrArgChange: false,
    refetchOnReconnect: false,
    refetchOnFocus: false
  });

  // const products = useMemo(() => (
  //   productsResponse ? productsResponse.pages.flatMap((page) => page.products) : []
  // ), [productsResponse]);


  const products = useMemo(() => {
    const pages = productsResponse?.pages || [];

    const map = new Map();

    pages.forEach(page => {
      // page.products might be undefined or the page might be an array itself
      const list = Array.isArray(page?.products) ? page.products : (Array.isArray(page) ? page : []);
      list.forEach(p => {
        // skip non-product objects like { msg: "No products found" }
        if (!p || typeof p !== 'object' || p.msg) return;

        // Same product can exist in multiple stores (e.g. Mango 053 + 054)
        const key = p.externalId ?? p.item_code ?? p._id ?? p.id ?? JSON.stringify(p);
        if (!map.has(key)) map.set(key, p);
      });
    });

    return Array.from(map.values());
  }, [productsResponse]);

  const addItemToWishlist = async (item) => {
    const updatedWishlist = [...(wishlistItems || [])];
    const index = updatedWishlist.findIndex(x => x?._id === item?._id);
    if (index === -1) {
      updatedWishlist.push(item);
    } else {
      updatedWishlist.splice(index, 1);
    }
    dispatch(addToWishlist(updatedWishlist));
  };

  const setCategoryToggleByLength = (e) => {
    setCategoryCheck(prev => {
      const safePrev = Array.isArray(prev) ? prev : [];
      // Convert single ID to array if needed
      const categoryArray = Array.isArray(e) ? e : [e];
      const categoryLength = categoryArray.length;
      
      // Check if an array with the same length exists
      const existsSameLength = safePrev.some(arr => 
        Array.isArray(arr) && 
        arr.length === categoryLength &&
        arr.every((id, idx) => String(id) === String(categoryArray[idx]))
      );
      
      if (existsSameLength) {
        // Remove the matching array
        const next = safePrev.filter(arr => 
          !(Array.isArray(arr) && 
            arr.length === categoryLength &&
            arr.every((id, idx) => String(id) === String(categoryArray[idx])))
        );
        return next;
      } else {
        // Add the new array
        const next = [...safePrev, categoryArray];
        return next;
      }
    });
  };

  const setBrandPush = (e) => setBrandCheck(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);
  const setSizePush = (e) => setSizeCheck(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);
  const setPricePush = (min, max) => {
    setGtPrice(min);
    setLtPrice(max);
  };

  const setSortPush = (e) => setSortPrice(e);
  const setOfferPushData = (e) => setOffers(e);
  const setGenderPushData = (e) => setSelectedGender(e);

  const resetFilter = () => {
    setSortPrice();
    // Preserve discount filter ONLY if we're on a discount=yes page (not discount=no)
    if (normalizedDiscount === 'yes') {
      // Keep the discount filter from URL
      setOffers(normalizedDiscount);
    } else {
      // Clear discount filter for all other cases (no discount param, discount=no, etc.)
      setOffers();
    }
    setSelectedGender();
    setLtPrice();
    setGtPrice();
    setBrandCheck([]);
    setSizeCheck([]);
    setCategoryCheck([]);
    
    // Clear sessionStorage to prevent filters from being restored (page-specific)
    try {
      sessionStorage.removeItem(filterStorageKey);
    } catch (e) {
      console.warn('Failed to clear filters from sessionStorage:', e);
    }
  };

  const loadedPagesCount = productsResponse?.pages?.length || 0;
  const productCount = productsResponse?.pages?.[0]?.totalCount;

  const handleLoadMore = () => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(urlPage + 1));
    setSearchParams(next, { replace: true });
    fetchNextPage();
  };

  // Auto infinite scroll should not mutate the URL page param (avoids "page jumps" on refresh).
  const handleAutoLoadMore = () => {
    fetchNextPage();
  };

  // Stable filters snapshot (used for PDP navigation and Back restore)
  const filtersForNavigation = useMemo(() => {
    return {
      categoryCheck,
      brandCheck,
      sizeCheck,
      gtPrice,
      ltPrice,
      sortPrice,
      offers,
      selectedGender
    };
  }, [categoryCheck, brandCheck, sizeCheck, gtPrice, ltPrice, sortPrice, offers, selectedGender]);

  // Persist filters snapshot to sessionStorage (page-specific)
  useEffect(() => {
    try {
      sessionStorage.setItem(
        filterStorageKey,
        JSON.stringify({ ...filtersForNavigation, timestamp: Date.now() })
      );
    } catch (e) {
      // ignore
    }
  }, [filterStorageKey, filtersForNavigation]);

  const handleProductClick = () => {
    // ProductItem ruan plp_scrollY + plp_productId
  };

  // Ensure we have loaded as many pages as URL indicates (por jo gjatë Back/POP)
  useEffect(() => {
    if (navigationType === 'POP') return;

    if (!urlPage) return;
    if (isLoading || isFetching) return;
    if (!hasNextPage) return;
    if (loadedPagesCount >= urlPage) return;
    fetchNextPage();
  }, [urlPage, loadedPagesCount, hasNextPage, isLoading, isFetching, fetchNextPage, navigationType]);

  // Reset URL page në 1 kur ndryshojnë argumentet kryesore të query-t
  const lastArgsKeyRef = useRef('');
  useEffect(() => {
    const argsKey = JSON.stringify(productsQueryParams);
    if (!lastArgsKeyRef.current) {
      lastArgsKeyRef.current = argsKey;
      return;
    }
    if (lastArgsKeyRef.current !== argsKey) {
      lastArgsKeyRef.current = argsKey;
      const next = new URLSearchParams(searchParams);
      next.set('page', '1');
      setSearchParams(next, { replace: true });
      try {
        sessionStorage.removeItem('plp_scrollY');
        sessionStorage.removeItem('plp_productId');
      } catch {}
    }
  }, [productsQueryParams, searchParams, setSearchParams]);

  // Scroll restoration (Back / POP)
  const didRestoreRef = useRef(false);
  useEffect(() => {
    if (didRestoreRef.current) return;
    if (navigationType !== 'POP') return;
    if (loadedPagesCount < urlPage) return;

    const productId = (() => {
      try { return sessionStorage.getItem('plp_productId'); } catch { return null; }
    })();
    const scrollStr = (() => {
      try { return sessionStorage.getItem('plp_scrollY'); } catch { return null; }
    })();

    // Asgjë për të rikthyer
    if (!productId && !scrollStr) return;

    // Sigurohu që browser-i të mos bëjë auto-scroll
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const initialY = (() => {
      if (!scrollStr) return null;
      const y = Number(scrollStr);
      return Number.isNaN(y) ? null : y;
    })();

    const alignToProduct = (attempt = 0) => {
      if (!productId) {
        didRestoreRef.current = true;
        try {
          sessionStorage.removeItem('plp_scrollY');
          sessionStorage.removeItem('plp_productId');
        } catch {}
        return;
      }

      const el = document.getElementById(`product-${productId}`);
      if (el || attempt >= 10) {
        if (el) {
          const header =
            document.querySelector('header') ??
            document.querySelector('.header') ??
            document.querySelector('[class*="header"]');
          const headerRect = header ? header.getBoundingClientRect() : null;
          const headerHeight = headerRect ? headerRect.height : 0;
          const top = el.getBoundingClientRect().top + window.scrollY - headerHeight - 8;
          window.scrollTo({ top, behavior: 'instant' });
        }

        didRestoreRef.current = true;
        try {
          sessionStorage.removeItem('plp_scrollY');
          sessionStorage.removeItem('plp_productId');
        } catch {}
        return;
      }

      window.setTimeout(() => alignToProduct(attempt + 1), 60);
    };

    const t = window.setTimeout(() => {
      // Hapi 1: shko afërsisht te pozicioni i ruajtur
      if (initialY !== null) {
        window.scrollTo(0, initialY);
      }
      // Hapi 2: pasi lista të jetë renderuar, ripozicionohu fiks te produkti
      alignToProduct(0);
    }, 50);

    return () => window.clearTimeout(t);
  }, [navigationType, loadedPagesCount, urlPage]);

  const loadingText =
    isLoading
      ? 'Loading products....'
      : (products.length === 0 && !isFetching ? 'Nuk ka produkte të disponueshme' : '');

  return (
    <div>
      <Header />
      <div ref={listContainerRef}>
        <div className="main_wrapper_products">
          {displayWidth < 767 ? (
            <div className="mobile-filter-bar">
            <Productmobilefilter
              setCategoryPushData={setCategoryToggleByLength}
              setBrandPushData={setBrandPush}
              setSizePushData={setSizePush}
              setPricePushData={setPricePush}
              setSortPushData={setSortPush}
              setOfferPush={setOfferPushData}
              setGenderPushData={setGenderPushData}
              filterBtn={() => { }}
              pageType="search"
              sortInBar
              resetFilter={resetFilter}
              activeOffer={offers}
              activeCategories={categoryCheck}
              activeBrands={brandCheck}
              activeSizes={sizeCheck}
              activeGender={selectedGender}
              activeSort={sortPrice}
              activeMinPrice={gtPrice}
              activeMaxPrice={ltPrice}
              productCount={productCount}
              isDiscountPage={normalizedDiscount === "yes"}
            />
            </div>
          ) : (
            <Productfilter
              setCategoryPushData={setCategoryToggleByLength}
              setBrandPushData={setBrandPush}
              setSizePushData={setSizePush}
              setPricePushData={setPricePush}
              setSortPushData={setSortPush}
              setOfferPush={setOfferPushData}
              setGenderPushData={setGenderPushData}
              filterBtn={() => { }}
              pageType="search"
              resetFilter={resetFilter}
              activeOffer={offers}
              activeCategories={categoryCheck}
              activeBrands={brandCheck}
              activeSizes={sizeCheck}
              activeGender={selectedGender}
              activeSort={sortPrice}
              activeMinPrice={gtPrice}
              activeMaxPrice={ltPrice}
              productCount={productCount}
              isDiscountPage={normalizedDiscount === "yes"}
            />
          )}
        </div>

        <div className={`w-full flex items-center justify-center ${products?.length === 0 ? 'no-rec-sec' : ''}`}>
          {isLoading ? (
            <div className="flex justify-center items-center h-12 w-12 animate-spin rounded-full border-4 border-solid border-current border-r-transparent">
              <span className="sr-only">Loading...</span>
            </div>
          ) : isError ? (
            <div className="text-center">
              <div>Gabim gjatë ngarkimit të produkteve. Provoni përsëri.</div>
              <button className="mt-2 px-4 py-2 bg-black text-white" onClick={() => refetch()}>Rifresko</button>
            </div>
          ) : (
            products.length === 0 && !isFetching && <div>{loadingText}</div>
          )}
        </div>

        <div className="mx-auto container product-list">
          <InfiniteProducts
            products={products}
            addItemToWishlist={addItemToWishlist}
            onProductClick={handleProductClick}
            onLoadMore={handleAutoLoadMore}
            hasNextPage={hasNextPage}
            isFetching={isFetching}
            filters={filtersForNavigation}
          />
        </div>
      </div>

      {/* Load More button */}
      {hasNextPage && !isFetching && (
        <div className="flex justify-center my-6">
          <button
            className="px-6 py-2 bg-black text-white rounded disabled:opacity-50"
            onClick={handleLoadMore}
            disabled={isFetching}
          >
            Shfaq më shumë produkte
          </button>
        </div>
      )}

      {/* Loading spinner for next pages */}
      {isFetching && !isLoading && (
        <div className="flex justify-center my-4">
          <div className="h-8 w-8 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
        </div>
      )}

      <Footer />
    </div>
  );
}
